/**
 * Product Image Zoom Modal for Shopify
 * ─────────────────────────────────────
 * Features:
 *  - Opens on click of any product image
 *  - Slider with thumbnails
 *  - Scroll / button zoom (1x → 1.5x → 2x → 3x)
 *  - Drag to pan when zoomed
 *  - Pinch-to-zoom on mobile
 *  - Keyboard: ←/→ navigate, +/- zoom, Esc close
 *
 * Usage in Shopify:
 *  1. Add this file to /assets/ as product-zoom-modal.js
 *  2. Add the CSS block to your theme.css or a <style> tag
 *  3. In product.liquid (or wherever images render), call:
 *       ProductZoomModal.init({
 *         triggerSelector: '.product__media img',   // adjust to your theme
 *         imageSelector:   '.product__media img',   // same or different set
 *       });
 *
 *  The script reads `data-zoom-src` (full-res) or falls back to `src`.
 *  In Liquid, set: <img data-zoom-src="{{ image | img_url: '2048x' }}" ...>
 */

(function () {
    'use strict';
  
    // ─── CONFIG ──────────────────────────────────────────────────────────────────
    const ZOOM_LEVELS   = [1, 1.5, 2, 3];
    const ANIM_DURATION = 200; // ms
  
    // ─── STATE ───────────────────────────────────────────────────────────────────
    let images      = [];   // [{ src, alt }]
    let currentIdx  = 0;
    let zoomLevel   = 1;    // actual scale value (not index)
    let zoomIdx     = 0;    // index into ZOOM_LEVELS
    let isDragging  = false;
    let dragStart   = { x: 0, y: 0 };
    let imgOffset   = { x: 0, y: 0 };
    let lastOffset  = { x: 0, y: 0 };
  
    // Pinch state
    let lastPinchDist = null;
    let pinchStartScale = 1;
  
    // ─── DOM REFERENCES ──────────────────────────────────────────────────────────
    let overlay, modal, imgEl, prevBtn, nextBtn, zoomInBtn, zoomOutBtn,
        closeBtn, thumbsWrapper, zoomIndicator;
  
    // ─── BUILD DOM ───────────────────────────────────────────────────────────────
    function buildModal() {
      overlay = el('div', 'pzm-overlay');
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', 'Image viewer');
  
      modal = el('div', 'pzm-modal');
  
      // Header
      const header = el('div', 'pzm-header');
      zoomIndicator = el('span', 'pzm-zoom-indicator', '1×');
      zoomInBtn  = iconBtn('pzm-btn pzm-zoom-in',  svgPlus(),  'Zoom in');
      zoomOutBtn = iconBtn('pzm-btn pzm-zoom-out', svgMinus(), 'Zoom out');
      closeBtn   = iconBtn('pzm-btn pzm-close',    svgClose(), 'Close');
      header.append(zoomIndicator, zoomInBtn, zoomOutBtn, closeBtn);
  
      // Stage
      const stage = el('div', 'pzm-stage');
      imgEl = el('img', 'pzm-img');
      imgEl.setAttribute('draggable', 'false');
      stage.appendChild(imgEl);
  
      // Nav
      prevBtn = iconBtn('pzm-btn pzm-prev', svgChevron('left'),  'Previous image');
      nextBtn = iconBtn('pzm-btn pzm-next', svgChevron('right'), 'Next image');
  
      // Thumbs
      thumbsWrapper = el('div', 'pzm-thumbs');
  
      modal.append(header, stage, prevBtn, nextBtn, thumbsWrapper);
      overlay.appendChild(modal);
      document.body.appendChild(overlay);
  
      bindEvents();
    }
  
    // ─── EVENTS ──────────────────────────────────────────────────────────────────
    function bindEvents() {
      closeBtn.addEventListener('click', close);
      overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  
      prevBtn.addEventListener('click', () => navigate(-1));
      nextBtn.addEventListener('click', () => navigate(1));
  
      zoomInBtn.addEventListener('click',  () => changeZoomByStep(1));
      zoomOutBtn.addEventListener('click', () => changeZoomByStep(-1));
  
      // Wheel zoom
      imgEl.addEventListener('wheel', onWheel, { passive: false });
  
      // Drag (mouse)
      imgEl.addEventListener('mousedown', onDragStart);
      document.addEventListener('mousemove', onDragMove);
      document.addEventListener('mouseup',   onDragEnd);
  
      // Touch
      imgEl.addEventListener('touchstart', onTouchStart, { passive: false });
      imgEl.addEventListener('touchmove',  onTouchMove,  { passive: false });
      imgEl.addEventListener('touchend',   onTouchEnd);
  
      // Keyboard
      document.addEventListener('keydown', onKeyDown);
    }
  
    // Wheel ─────────────────────────────────────────────────────────────────
    function onWheel(e) {
      e.preventDefault();
      if (e.deltaY < 0) changeZoomByStep(1);
      else               changeZoomByStep(-1);
    }
  
    // Mouse drag ────────────────────────────────────────────────────────────
    function onDragStart(e) {
      if (zoomLevel <= 1) return;
      isDragging = true;
      dragStart  = { x: e.clientX - lastOffset.x, y: e.clientY - lastOffset.y };
      imgEl.style.cursor = 'grabbing';
    }
    function onDragMove(e) {
      if (!isDragging) return;
      imgOffset = {
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      };
      applyTransform(false);
    }
    function onDragEnd() {
      if (!isDragging) return;
      isDragging  = false;
      lastOffset  = { ...imgOffset };
      imgEl.style.cursor = zoomLevel > 1 ? 'grab' : '';
    }
  
    // Touch ─────────────────────────────────────────────────────────────────
    function onTouchStart(e) {
      if (e.touches.length === 2) {
        lastPinchDist   = getPinchDist(e);
        pinchStartScale = zoomLevel;
        return;
      }
      if (zoomLevel > 1) {
        isDragging = true;
        dragStart  = { x: e.touches[0].clientX - lastOffset.x, y: e.touches[0].clientY - lastOffset.y };
      }
    }
    function onTouchMove(e) {
      e.preventDefault();
      if (e.touches.length === 2) {
        const dist  = getPinchDist(e);
        const ratio = dist / lastPinchDist;
        let   newScale = clamp(pinchStartScale * ratio, ZOOM_LEVELS[0], ZOOM_LEVELS[ZOOM_LEVELS.length - 1]);
        setZoom(newScale, false);
        return;
      }
      if (!isDragging) return;
      imgOffset = {
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y,
      };
      applyTransform(false);
    }
    function onTouchEnd(e) {
      if (e.touches.length < 2) lastPinchDist = null;
  
      if (isDragging) {
        isDragging = false;
        lastOffset = { ...imgOffset };
      }
    }
  
    // Keyboard ──────────────────────────────────────────────────────────────
    function onKeyDown(e) {
      if (!overlay.classList.contains('is-open')) return;
      switch (e.key) {
        case 'ArrowLeft':  navigate(-1);          break;
        case 'ArrowRight': navigate(1);           break;
        case '+': case '=': changeZoomByStep(1);  break;
        case '-':            changeZoomByStep(-1); break;
        case 'Escape':       close();             break;
      }
    }
  
    // ─── OPEN / CLOSE ────────────────────────────────────────────────────────────
    function open(idx) {
      currentIdx = idx;
      resetZoom();
      renderThumbs();
      loadImage(idx);
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      closeBtn.focus();
    }
  
    function close() {
      overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  
    // ─── NAVIGATION ──────────────────────────────────────────────────────────────
    function navigate(dir) {
      const next = (currentIdx + dir + images.length) % images.length;
      currentIdx = next;
      resetZoom();
      loadImage(next);
      updateThumbs();
      prevBtn.disabled = images.length <= 1;
      nextBtn.disabled = images.length <= 1;
    }
  
    function loadImage(idx) {
      imgEl.style.opacity = '0';
      imgEl.src = images[idx].src;
      imgEl.alt = images[idx].alt || '';
      imgEl.onload = () => {
        imgEl.style.opacity = '1';
      };
    }
  
    // ─── ZOOM ────────────────────────────────────────────────────────────────────
    function changeZoomByStep(dir) {
      zoomIdx = clamp(zoomIdx + dir, 0, ZOOM_LEVELS.length - 1);
      setZoom(ZOOM_LEVELS[zoomIdx], true);
    }
  
    function setZoom(scale, animate) {
      zoomLevel = scale;
      // Find closest index
      zoomIdx = ZOOM_LEVELS.reduce((best, lvl, i) =>
        Math.abs(lvl - scale) < Math.abs(ZOOM_LEVELS[best] - scale) ? i : best, 0);
  
      if (scale <= 1) {
        imgOffset = { x: 0, y: 0 };
        lastOffset = { x: 0, y: 0 };
      }
  
      applyTransform(animate);
      zoomIndicator.textContent = `${Math.round(scale * 100) / 100}×`;
      imgEl.style.cursor = scale > 1 ? 'grab' : '';
      zoomOutBtn.disabled = zoomIdx === 0;
      zoomInBtn.disabled  = zoomIdx === ZOOM_LEVELS.length - 1;
    }
  
    function applyTransform(animate) {
      imgEl.style.transition = animate ? `transform ${ANIM_DURATION}ms ease` : 'none';
      imgEl.style.transform  = `translate(${imgOffset.x}px, ${imgOffset.y}px) scale(${zoomLevel})`;
    }
  
    function resetZoom() {
      zoomIdx    = 0;
      zoomLevel  = 1;
      imgOffset  = { x: 0, y: 0 };
      lastOffset = { x: 0, y: 0 };
      if (imgEl) {
        imgEl.style.transition = 'none';
        imgEl.style.transform  = 'none';
      }
      if (zoomIndicator) zoomIndicator.textContent = '1×';
      if (zoomOutBtn)    zoomOutBtn.disabled = true;
      if (zoomInBtn)     zoomInBtn.disabled  = false;
    }
  
    // ─── THUMBNAILS ──────────────────────────────────────────────────────────────
    function renderThumbs() {
      thumbsWrapper.innerHTML = '';
      images.forEach((img, i) => {
        const t = el('button', 'pzm-thumb' + (i === currentIdx ? ' is-active' : ''));
        const tImg = el('img');
        tImg.src = img.thumb || img.src;
        tImg.alt = img.alt || '';
        t.appendChild(tImg);
        t.addEventListener('click', () => {
          currentIdx = i;
          resetZoom();
          loadImage(i);
          updateThumbs();
        });
        thumbsWrapper.appendChild(t);
      });
    }
  
    function updateThumbs() {
      thumbsWrapper.querySelectorAll('.pzm-thumb').forEach((t, i) => {
        t.classList.toggle('is-active', i === currentIdx);
      });
      // Scroll active thumb into view
      const active = thumbsWrapper.querySelector('.is-active');
      if (active) active.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'nearest' });
    }
  
    // ─── PUBLIC INIT ─────────────────────────────────────────────────────────────
    /**
     * @param {Object} opts
     * @param {string} opts.triggerSelector  - CSS selector for clickable elements
     * @param {string} [opts.imageSelector]  - CSS selector to collect images for gallery
     *                                         (defaults to triggerSelector)
     * @param {Function} [opts.getImages]    - Override: return array of {src, thumb, alt}
     */
    function init(opts) {
      if (!opts || !opts.triggerSelector) {
        console.warn('[ProductZoomModal] triggerSelector is required.');
        return;
      }
  
      buildModal();
  
      document.querySelectorAll(opts.triggerSelector).forEach((trigger, idx) => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
  
          // Collect images lazily on first click
          if (typeof opts.getImages === 'function') {
            images = opts.getImages();
          } else {
            const sel = opts.imageSelector || opts.triggerSelector;
            images = Array.from(document.querySelectorAll(sel)).map((img) => ({
              src:   img.dataset.zoomSrc || img.src,
              thumb: img.src,
              alt:   img.alt,
            }));
          }
  
          // Find which index was clicked
          const sel = opts.imageSelector || opts.triggerSelector;
          const allEls = Array.from(document.querySelectorAll(sel));
          const clickedIdx = allEls.indexOf(
            trigger.tagName === 'IMG' ? trigger : trigger.querySelector('img')
          );
  
          open(clickedIdx >= 0 ? clickedIdx : idx);
        });
      });
    }
  
    // ─── HELPERS ─────────────────────────────────────────────────────────────────
    function el(tag, className, text) {
      const e = document.createElement(tag);
      if (className) e.className = className;
      if (text)      e.textContent = text;
      return e;
    }
  
    function iconBtn(cls, svgStr, label) {
      const b = el('button', cls);
      b.setAttribute('aria-label', label);
      b.innerHTML = svgStr;
      return b;
    }
  
    function clamp(v, min, max) { return Math.min(Math.max(v, min), max); }
  
    function getPinchDist(e) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }
  
    // ─── SVG ICONS ───────────────────────────────────────────────────────────────
    function svgClose() {
      return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
      </svg>`;
    }
    function svgPlus() {
      return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
      </svg>`;
    }
    function svgMinus() {
      return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
        <line x1="5" y1="12" x2="19" y2="12"/>
      </svg>`;
    }
    function svgChevron(dir) {
        const d = dir === 'left'
          ? 'M15 18l-6-6 6-6'
          : 'M9 18l6-6-6-6';
        return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="${d}"/>
        </svg>`;
    }
  
    // ─── EXPORT ──────────────────────────────────────────────────────────────────
    window.ProductZoomModal = { init };
  })();